import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Switch,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { RescheduleScheduleStyled } from "./reschedule-schedule.styles";
import { TimePickerField } from "../../../../components/form-fields/time-picker-field/time-picker-field";
import dayjs from "dayjs";
import {
  getRescheduleSchedules,
  createRescheduleSchedule,
  updateRescheduleSchedule,
  deleteRescheduleSchedule,
  runReschedule,
  getRescheduleLatestResult,
} from "../../../../actions";
import { Button } from "../../../../components/button/button";
import Loader from "../../../../components/loader/loader";
import { addNotification } from "../../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { useAppDispatch } from "../../../../store/hooks";

interface Schedule {
  id: number;
  time: string;
  days_of_week: number[];
  timezone: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RunResultSummary {
  offers_sent: string[];
  no_slot_found: string[];
  removed: string[];
  other: string[];
}

interface RunResult {
  id: number;
  status: string;
  is_dry_run: boolean;
  triggered_by: string | null;
  summary: RunResultSummary | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

const DAY_LABELS: Record<number, string> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

const DEFAULT_FORM: { time: string; days_of_week: number[]; timezone: string; is_active: boolean } = {
  time: "12:00",
  days_of_week: [1, 2, 3, 4],
  timezone: "America/Chicago",
  is_active: true,
};

const RescheduleScheduleTab: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [running, setRunning] = useState(false);
  const [latestResult, setLatestResult] = useState<RunResult | null>(null);
  const dispatch = useAppDispatch();

  const fetchSchedules = useCallback(async () => {
    try {
      const response = await getRescheduleSchedules();
      setSchedules(response.data?.data || []);
    } catch (error) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Failed to fetch reschedule schedules",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchLatestResult = useCallback(async () => {
    try {
      const response = await getRescheduleLatestResult();
      setLatestResult(response.data?.data || null);
    } catch (error) {
      // Silently fail — not critical
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
    fetchLatestResult();
  }, [fetchSchedules, fetchLatestResult]);

  const handleToggleActive = async (schedule: Schedule) => {
    try {
      await updateRescheduleSchedule(schedule.id, {
        is_active: !schedule.is_active,
      });
      fetchSchedules();
    } catch (error) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Failed to update schedule",
        })
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteRescheduleSchedule(id);
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: "Schedule deleted",
        })
      );
      fetchSchedules();
    } catch (error) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Failed to delete schedule",
        })
      );
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setEditingId(schedule.id);
    setForm({
      time: schedule.time.substring(0, 5),
      days_of_week: schedule.days_of_week,
      timezone: schedule.timezone,
      is_active: schedule.is_active,
    });
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(DEFAULT_FORM);
  };

  const handleDayToggle = (day: number) => {
    setForm((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter((d) => d !== day)
        : [...prev.days_of_week, day].sort(),
    }));
  };

  const handleSubmit = async () => {
    if (form.days_of_week.length === 0) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Select at least one day",
        })
      );
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await updateRescheduleSchedule(editingId, form);
      } else {
        await createRescheduleSchedule(form);
      }
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: editingId ? "Schedule updated" : "Schedule created",
        })
      );
      handleCancel();
      fetchSchedules();
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to save schedule",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRun = async (dryRun: boolean) => {
    setRunning(true);
    try {
      const response = await runReschedule({ dry_run: dryRun });
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: response.data?.message || "Reschedule dispatched",
        })
      );
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to dispatch reschedule",
        })
      );
    } finally {
      setRunning(false);
    }
  };

  const formatTime12h = (time: string) => {
    const [h, m] = time.split(":");
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const formatDatetime = (dt: string) => {
    const date = new Date(dt);
    return date.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) return <Loader absolute />;

  return (
    <RescheduleScheduleStyled>
      <Box className="header">
        <Typography variant="h2">Reschedule Schedule</Typography>
        <Box className="header-actions">
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleRun(false)}
            disabled={running}
            isLoading={running}
          >
            Run Now
          </Button>
          <Button
            variant="outlined"
            onClick={fetchLatestResult}
          >
            Refresh Results
          </Button>
          {!showForm && (
            <Button variant="contained" color="primary" onClick={handleAdd}>
              Add Schedule
            </Button>
          )}
        </Box>
      </Box>

      {showForm && (
        <Box className="form-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingId ? "Edit Schedule" : "New Schedule"}
          </Typography>
          <Box className="form-fields">
            <TimePickerField
              label="Time"
              value={dayjs(form.time, "HH:mm").format("hh:mm A")}
              onChange={(val: string) => {
                const parsed = dayjs(val, "hh:mm A");
                if (parsed.isValid()) {
                  setForm({ ...form, time: parsed.format("HH:mm") });
                }
              }}
            />
            <TextField
              label="Timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              fullWidth
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Days of Week
          </Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    checked={form.days_of_week.includes(day)}
                    onChange={() => handleDayToggle(day)}
                  />
                }
                label={DAY_LABELS[day]}
              />
            ))}
          </FormGroup>
          <Box className="form-actions">
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting}
              isLoading={submitting}
            >
              {editingId ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      )}

      <Box className="schedule-list">
        {schedules.length === 0 && (
          <Typography color="textSecondary">
            No reschedule schedules configured.
          </Typography>
        )}
        {schedules.map((schedule) => (
          <Box key={schedule.id} className="schedule-card">
            <Box className="schedule-info">
              <Typography variant="h6">
                {formatTime12h(schedule.time)}
              </Typography>
              <Box className="day-chips">
                {schedule.days_of_week.map((day) => (
                  <Chip
                    key={day}
                    label={DAY_LABELS[day]}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography variant="body2" color="textSecondary">
                {schedule.timezone}
              </Typography>
            </Box>
            <Box className="schedule-actions">
              <Switch
                checked={schedule.is_active}
                onChange={() => handleToggleActive(schedule)}
                color="primary"
              />
              <IconButton onClick={() => handleEdit(schedule)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(schedule.id)}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>

      {latestResult && (
        <Box className="result-card">
          <Box className="result-header">
            <Typography variant="h6">Last Run</Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip
                label={latestResult.status}
                size="small"
                color={
                  latestResult.status === "completed"
                    ? "success"
                    : latestResult.status === "running"
                    ? "warning"
                    : "error"
                }
              />
              {latestResult.is_dry_run && (
                <Chip label="Dry Run" size="small" variant="outlined" />
              )}
              {latestResult.triggered_by && (
                <Typography variant="body2" color="textSecondary">
                  by {latestResult.triggered_by}
                </Typography>
              )}
              {latestResult.completed_at && (
                <Typography variant="body2" color="textSecondary">
                  {formatDatetime(latestResult.completed_at)}
                </Typography>
              )}
            </Box>
          </Box>

          {latestResult.error && (
            <Box className="result-section">
              <Typography variant="subtitle2" color="error">
                Error
              </Typography>
              <Typography variant="body2">{latestResult.error}</Typography>
            </Box>
          )}

          {latestResult.summary && (
            <Box className="result-sections">
              {latestResult.summary.offers_sent.length > 0 && (
                <Box className="result-section">
                  <Typography variant="subtitle2" color="success.main">
                    Offers Sent ({latestResult.summary.offers_sent.length})
                  </Typography>
                  {latestResult.summary.offers_sent.map((line, i) => (
                    <Typography key={i} variant="body2">{line}</Typography>
                  ))}
                </Box>
              )}
              {latestResult.summary.no_slot_found.length > 0 && (
                <Box className="result-section">
                  <Typography variant="subtitle2" color="text.secondary">
                    No Earlier Slot Found ({latestResult.summary.no_slot_found.length})
                  </Typography>
                  {latestResult.summary.no_slot_found.map((line, i) => (
                    <Typography key={i} variant="body2">{line}</Typography>
                  ))}
                </Box>
              )}
              {latestResult.summary.removed.length > 0 && (
                <Box className="result-section">
                  <Typography variant="subtitle2" color="warning.main">
                    Removed ({latestResult.summary.removed.length})
                  </Typography>
                  {latestResult.summary.removed.map((line, i) => (
                    <Typography key={i} variant="body2">{line}</Typography>
                  ))}
                </Box>
              )}
              {latestResult.summary.offers_sent.length === 0 &&
                latestResult.summary.no_slot_found.length === 0 &&
                latestResult.summary.removed.length === 0 && (
                  <Typography variant="body2" color="textSecondary">
                    No reschedulable appointments to process.
                  </Typography>
                )}
            </Box>
          )}
        </Box>
      )}
    </RescheduleScheduleStyled>
  );
};

export default RescheduleScheduleTab;
